import { Box, IconButton, useColorModeValue } from '@chakra-ui/react'
import { Ref, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { StringFilterInput } from '../../../gql/graphql'
import { RouteConfig } from '../../../router/routes'
import { ViewUrlState } from '../../../store/appStore/view/viewSlice.types'
import { colors } from '../../../theme/colors'
import { Icon } from '../../Icons/Icon'
import { SearchIcon } from '../../Icons/SearchIcon'
import { BaseInput, BaseInputProps } from '../BaseInput'

export interface SearchTextInputProps extends BaseInputProps {
  route: RouteConfig
  filterKey: string
  queryId: string
  ref?: Ref<HTMLInputElement>
}

/**
 * Converts input value to filter input format.
 * Auto-selects condition based on input value:
 * - If contains comma(s) → use "In List" (exact matches from comma-separated list)
 * - Otherwise → use "Contains" (partial match)
 */
const valueToFilterInput = (value: string): StringFilterInput => {
  const trimmedValue = value.trim()
  const hasComma = trimmedValue.includes(',')

  if (hasComma) {
    const values = trimmedValue
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0)
    return {
      in: values,
      mode: 'insensitive',
    }
  }

  return {
    contains: trimmedValue,
    mode: 'insensitive',
  }
}

const homeDashboardAccentTeal = colors.page.light.homeDashboard.accentTeal
const homeDashboardIconOnTeal = colors.page.light.homeDashboard.iconOnFilledTeal
const homeDashboardInputRouteIcon =
  colors.page.light.homeDashboard.inputRouteIcon

export const SearchTextInput = ({
  route,
  filterKey,
  queryId,
  placeholder,
  ref,
  ...props
}: SearchTextInputProps) => {
  const [inputValue, setInputValue] = useState('')

  const navigate = useNavigate()
  const routeIconColor = useColorModeValue(
    homeDashboardInputRouteIcon,
    'presets.neutral.0',
  )
  const searchButtonBg = useColorModeValue(
    homeDashboardAccentTeal,
    'presets.turquoise.60',
  )
  const searchIconColor = useColorModeValue(
    homeDashboardIconOnTeal,
    'presets.neutral.0',
  )

  const handleSubmit = () => {
    const trimmedValue = inputValue.trim()
    if (!trimmedValue) return

    const filterInput = valueToFilterInput(trimmedValue)

    const viewUrlState: ViewUrlState = {
      queryStates: [
        {
          queryId: queryId,
          variables: {
            filter: {
              [filterKey]: filterInput,
            },
          },
        },
      ],
    }

    const searchParams = new URLSearchParams({
      viewState: JSON.stringify(viewUrlState),
    })

    navigate(`${route.path}?${searchParams.toString()}`)
  }

  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <BaseInput
        ref={ref}
        type="text"
        variant="search"
        value={inputValue}
        placeholder={placeholder}
        onChange={event => {
          setInputValue(event.target.value)
        }}
        leftInputElement={
          <Box paddingLeft="8px">
            <Icon
              icon={route?.routeIconName!}
              boxSize="20px"
              color={routeIconColor}
            />
          </Box>
        }
        rightInputElement={
          <IconButton
            aria-label="Search"
            icon={<SearchIcon boxSize="18px" />}
            bg={searchButtonBg}
            color={searchIconColor}
            borderRadius="full"
            boxSize="32px"
            minW="32px"
            minH="32px"
            padding="0"
            _hover={{
              bg: 'presets.turquoise.70',
              color: searchIconColor,
            }}
            _active={{
              bg: 'presets.turquoise.70',
              color: searchIconColor,
            }}
            onClick={handleSubmit}
          />
        }
        {...props}
      />
    </form>
  )
}
